import { renameKeysFromRESTResponseToSDKResponse } from '@wix/sdk-runtime/rename-all-nested-keys';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { EventDefinition, HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  AddDeliveryCarrierApplicationErrors,
  AddDeliveryCarrierOptions,
  AddDeliveryCarrierResponse,
  AddDeliveryCarrierValidationErrors,
  AddDeliveryRegionApplicationErrors,
  AddDeliveryRegionOptions,
  AddDeliveryRegionResponse,
  AddDeliveryRegionValidationErrors,
  CreateDeliveryProfileApplicationErrors,
  DeliveryProfile,
  DeliveryProfileCreatedEnvelope,
  DeliveryProfileDeletedEnvelope,
  DeliveryProfileQuery,
  DeliveryProfileRegionAddedEnvelope,
  DeliveryProfileRegionRemovedEnvelope,
  DeliveryProfileRegionUpdatedEnvelope,
  DeliveryProfileUpdatedEnvelope,
  DeliveryProfilesQueryBuilder,
  DeliveryRegion,
  GetDeliveryDestinationPropertiesResponse,
  GetDeliveryProfileByDeliveryRegionIdApplicationErrors,
  GetDeliveryProfileByDeliveryRegionIdResponse,
  ListDeliveryCarriersApplicationErrors,
  ListDeliveryCarriersOptions,
  ListDeliveryCarriersResponse,
  ListInstalledDeliveryCarriersResponse,
  QueryDeliveryProfilesResponse,
  RemoveDeliveryCarrierApplicationErrors,
  RemoveDeliveryCarrierOptions,
  RemoveDeliveryCarrierResponse,
  RemoveDeliveryRegionApplicationErrors,
  RemoveDeliveryRegionIdentifiers,
  RemoveDeliveryRegionOptions,
  RemoveDeliveryRegionResponse,
  SetDeliveryCarrierActiveStatusApplicationErrors,
  SetDeliveryCarrierActiveStatusOptions,
  UpdateCarrierSettingsApplicationErrors,
  UpdateCarrierSettingsOptions,
  UpdateDeliveryCarrierApplicationErrors,
  UpdateDeliveryCarrierOptions,
  UpdateDeliveryCarrierResponse,
  UpdateDeliveryProfile,
  UpdateDeliveryProfileApplicationErrors,
  UpdateDeliveryRegionApplicationErrors,
  UpdateDeliveryRegionIdentifiers,
  UpdateDeliveryRegionOptions,
  UpdateDeliveryRegionResponse,
  UpdateDeliveryRegionValidationErrors,
  UpdateExtendedFieldsOptions,
  UpdateExtendedFieldsResponse,
  addDeliveryCarrier as universalAddDeliveryCarrier,
  addDeliveryRegion as universalAddDeliveryRegion,
  createDeliveryProfile as universalCreateDeliveryProfile,
  deleteDeliveryProfile as universalDeleteDeliveryProfile,
  getDeliveryDestinationProperties as universalGetDeliveryDestinationProperties,
  getDeliveryProfile as universalGetDeliveryProfile,
  getDeliveryProfileByDeliveryRegionId as universalGetDeliveryProfileByDeliveryRegionId,
  listDeliveryCarriers as universalListDeliveryCarriers,
  listInstalledDeliveryCarriers as universalListInstalledDeliveryCarriers,
  queryDeliveryProfiles as universalQueryDeliveryProfiles,
  removeDeliveryCarrier as universalRemoveDeliveryCarrier,
  removeDeliveryRegion as universalRemoveDeliveryRegion,
  setDeliveryCarrierActiveStatus as universalSetDeliveryCarrierActiveStatus,
  typedQueryDeliveryProfiles as universalTypedQueryDeliveryProfiles,
  updateCarrierSettings as universalUpdateCarrierSettings,
  updateDeliveryCarrier as universalUpdateDeliveryCarrier,
  updateDeliveryProfile as universalUpdateDeliveryProfile,
  updateDeliveryRegion as universalUpdateDeliveryRegion,
  updateExtendedFields as universalUpdateExtendedFields,
} from './ecom-v1-delivery-profile-delivery-profile.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/ecom' };

export function createDeliveryProfile(
  httpClient: HttpClient
): CreateDeliveryProfileSignature {
  return (
    deliveryProfile: NonNullablePaths<
      DeliveryProfile,
      | `deliveryRegions.${number}.destinations.${number}.countryCode`
      | `deliveryRegions.${number}.name`
      | `name`,
      6
    >
  ) =>
    universalCreateDeliveryProfile(
      deliveryProfile,
      // @ts-ignore
      { httpClient }
    );
}

interface CreateDeliveryProfileSignature {
  /**
   * Creates a delivery profile.
   *
   *
   * A site can have up to 99 delivery profiles.
   * Attempting to exceed this limit returns a `DELIVERY_PROFILES_LIMIT_EXCEEDED` error.
   * @param - Delivery profile to create.
   * @returns Created delivery profile.
   */
  (
    deliveryProfile: NonNullablePaths<
      DeliveryProfile,
      | `deliveryRegions.${number}.destinations.${number}.countryCode`
      | `deliveryRegions.${number}.name`
      | `name`,
      6
    >
  ): Promise<
    NonNullablePaths<DeliveryProfile, `deliveryRegions`, 2> & {
      __applicationErrorsType?: CreateDeliveryProfileApplicationErrors;
    }
  >;
}

export function getDeliveryProfile(
  httpClient: HttpClient
): GetDeliveryProfileSignature {
  return (deliveryProfileId: string) =>
    universalGetDeliveryProfile(
      deliveryProfileId,
      // @ts-ignore
      { httpClient }
    );
}

interface GetDeliveryProfileSignature {
  /**
   * Retrieves a delivery profile.
   * @param - Delivery profile ID.
   * @returns Retrieved delivery profile.
   */
  (deliveryProfileId: string): Promise<
    NonNullablePaths<DeliveryProfile, `deliveryRegions`, 2>
  >;
}

export function updateDeliveryProfile(
  httpClient: HttpClient
): UpdateDeliveryProfileSignature {
  return (
    _id: string,
    deliveryProfile: NonNullablePaths<UpdateDeliveryProfile, `revision`, 2>
  ) =>
    universalUpdateDeliveryProfile(
      _id,
      deliveryProfile,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdateDeliveryProfileSignature {
  /**
   * Updates a delivery profile.
   *
   * Each time the delivery profile is updated, `revision` increments by 1. The current `revision` must be passed when updating the delivery profile. This ensures you're working with the latest delivery profile and prevents unintended overwrites.
   *
   * This method doesn't support updating delivery regions in the profile. To manage delivery regions, use [Add Delivery Region](https://dev.wix.com/docs/api-reference/business-solutions/e-commerce/shipping-delivery/delivery-profiles/add-delivery-region), [Update Delivery Region](https://dev.wix.com/docs/api-reference/business-solutions/e-commerce/shipping-delivery/delivery-profiles/update-delivery-region), or [Remove Delivery Region](https://dev.wix.com/docs/api-reference/business-solutions/e-commerce/shipping-delivery/delivery-profiles/remove-delivery-region).
   * @param - Delivery profile ID.
   * @returns Updated delivery profile.
   */
  (
    _id: string,
    deliveryProfile: NonNullablePaths<UpdateDeliveryProfile, `revision`, 2>
  ): Promise<
    NonNullablePaths<DeliveryProfile, `deliveryRegions`, 2> & {
      __applicationErrorsType?: UpdateDeliveryProfileApplicationErrors;
    }
  >;
}

export function deleteDeliveryProfile(
  httpClient: HttpClient
): DeleteDeliveryProfileSignature {
  return (deliveryProfileId: string) =>
    universalDeleteDeliveryProfile(
      deliveryProfileId,
      // @ts-ignore
      { httpClient }
    );
}

interface DeleteDeliveryProfileSignature {
  /**
   * Deletes a delivery profile.
   *
   *
   * Permanently deletes the delivery profile and its delivery regions and carrier configurations. Delivery regions are unique to each profile and aren't shared across profiles. This action can't be undone.
   *
   * The default delivery profile can't be deleted because every site requires one for core delivery functionality.
   * @param - Delivery profile ID.
   */
  (deliveryProfileId: string): Promise<void>;
}

export function queryDeliveryProfiles(
  httpClient: HttpClient
): QueryDeliveryProfilesSignature {
  return () =>
    universalQueryDeliveryProfiles(
      // @ts-ignore
      { httpClient }
    );
}

interface QueryDeliveryProfilesSignature {
  /**
   * Retrieves a list of up to 100 delivery profiles, given the provided paging, filtering, and sorting.
   *
   * Query Delivery Profiles runs with these defaults, which you can override:
   *
   * - `createdDate` is sorted in `DESC` order.
   * - `paging.limit` is `50`.
   * - `paging.offset` is `0`.
   *
   * To learn how to query delivery profiles, see [API Query Language](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-the-wix-api-query-language).
   */
  (): DeliveryProfilesQueryBuilder;
}

export function typedQueryDeliveryProfiles(
  httpClient: HttpClient
): TypedQueryDeliveryProfilesSignature {
  return (query: DeliveryProfileQuery) =>
    universalTypedQueryDeliveryProfiles(
      query,
      // @ts-ignore
      { httpClient }
    );
}

interface TypedQueryDeliveryProfilesSignature {
  /** */
  (query: DeliveryProfileQuery): Promise<
    NonNullablePaths<QueryDeliveryProfilesResponse, `deliveryProfiles`, 2>
  >;
}

export function addDeliveryRegion(
  httpClient: HttpClient
): AddDeliveryRegionSignature {
  return (
    deliveryProfileId: string,
    deliveryRegion: NonNullablePaths<
      DeliveryRegion,
      `destinations.${number}.countryCode` | `name`,
      4
    >,
    options?: AddDeliveryRegionOptions
  ) =>
    universalAddDeliveryRegion(
      deliveryProfileId,
      deliveryRegion,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface AddDeliveryRegionSignature {
  /**
   * Adds a delivery region to an existing delivery profile.
   * @param - Delivery profile ID.
   * @param - Delivery region to add.
   */
  (
    deliveryProfileId: string,
    deliveryRegion: NonNullablePaths<
      DeliveryRegion,
      `destinations.${number}.countryCode` | `name`,
      4
    >,
    options?: AddDeliveryRegionOptions
  ): Promise<
    NonNullablePaths<
      AddDeliveryRegionResponse,
      `deliveryProfile.deliveryRegions`,
      3
    > & {
      __applicationErrorsType?: AddDeliveryRegionApplicationErrors;
      __validationErrorsType?: AddDeliveryRegionValidationErrors;
    }
  >;
}

export function updateDeliveryRegion(
  httpClient: HttpClient
): UpdateDeliveryRegionSignature {
  return (
    identifiers: NonNullablePaths<
      UpdateDeliveryRegionIdentifiers,
      `deliveryProfileId`,
      2
    >,
    deliveryRegion: DeliveryRegion,
    options?: UpdateDeliveryRegionOptions
  ) =>
    universalUpdateDeliveryRegion(
      identifiers,
      deliveryRegion,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdateDeliveryRegionSignature {
  /**
   * Updates a delivery region.
   *
   *
   * This method doesn't support updating delivery carriers in the region. To manage carriers, use [Add Delivery Carrier](https://dev.wix.com/docs/api-reference/business-solutions/e-commerce/shipping-delivery/delivery-profiles/add-delivery-carrier), [Update Delivery Carrier](https://dev.wix.com/docs/api-reference/business-solutions/e-commerce/shipping-delivery/delivery-profiles/update-delivery-carrier), or [Remove Delivery Carrier](https://dev.wix.com/docs/api-reference/business-solutions/e-commerce/shipping-delivery/delivery-profiles/remove-delivery-carrier).
   * @param - Delivery region to update.
   */
  (
    identifiers: NonNullablePaths<
      UpdateDeliveryRegionIdentifiers,
      `deliveryProfileId`,
      2
    >,
    deliveryRegion: DeliveryRegion,
    options?: UpdateDeliveryRegionOptions
  ): Promise<
    NonNullablePaths<
      UpdateDeliveryRegionResponse,
      `deliveryProfile.deliveryRegions`,
      3
    > & {
      __applicationErrorsType?: UpdateDeliveryRegionApplicationErrors;
      __validationErrorsType?: UpdateDeliveryRegionValidationErrors;
    }
  >;
}

export function getDeliveryProfileByDeliveryRegionId(
  httpClient: HttpClient
): GetDeliveryProfileByDeliveryRegionIdSignature {
  return (deliveryRegionId: string) =>
    universalGetDeliveryProfileByDeliveryRegionId(
      deliveryRegionId,
      // @ts-ignore
      { httpClient }
    );
}

interface GetDeliveryProfileByDeliveryRegionIdSignature {
  /**
   * Retrieves a delivery profile by delivery region ID.
   * @param - Delivery region ID.
   */
  (deliveryRegionId: string): Promise<
    NonNullablePaths<
      GetDeliveryProfileByDeliveryRegionIdResponse,
      `deliveryProfile.deliveryRegions`,
      3
    > & {
      __applicationErrorsType?: GetDeliveryProfileByDeliveryRegionIdApplicationErrors;
    }
  >;
}

export function removeDeliveryRegion(
  httpClient: HttpClient
): RemoveDeliveryRegionSignature {
  return (
    identifiers: NonNullablePaths<
      RemoveDeliveryRegionIdentifiers,
      `deliveryProfileId` | `deliveryRegionId`,
      2
    >,
    options?: RemoveDeliveryRegionOptions
  ) =>
    universalRemoveDeliveryRegion(
      identifiers,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface RemoveDeliveryRegionSignature {
  /**
   * Removes a delivery region from a delivery profile.
   *
   *
   * You can remove all delivery regions from a profile, leaving the profile empty. An empty profile won't provide shipping options until a new delivery region is added.
   */
  (
    identifiers: NonNullablePaths<
      RemoveDeliveryRegionIdentifiers,
      `deliveryProfileId` | `deliveryRegionId`,
      2
    >,
    options?: RemoveDeliveryRegionOptions
  ): Promise<
    NonNullablePaths<
      RemoveDeliveryRegionResponse,
      `deliveryProfile.deliveryRegions`,
      3
    > & {
      __applicationErrorsType?: RemoveDeliveryRegionApplicationErrors;
    }
  >;
}

export function addDeliveryCarrier(
  httpClient: HttpClient
): AddDeliveryCarrierSignature {
  return (
    deliveryRegionId: string,
    options: NonNullablePaths<
      AddDeliveryCarrierOptions,
      `deliveryCarrier` | `deliveryCarrier.backupRate.amount`,
      4
    >
  ) =>
    universalAddDeliveryCarrier(
      deliveryRegionId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface AddDeliveryCarrierSignature {
  /**
   * Adds a delivery carrier to a delivery region.
   * @param - Delivery region ID.
   */
  (
    deliveryRegionId: string,
    options: NonNullablePaths<
      AddDeliveryCarrierOptions,
      `deliveryCarrier` | `deliveryCarrier.backupRate.amount`,
      4
    >
  ): Promise<
    NonNullablePaths<
      AddDeliveryCarrierResponse,
      `deliveryProfile.deliveryRegions`,
      3
    > & {
      __applicationErrorsType?: AddDeliveryCarrierApplicationErrors;
      __validationErrorsType?: AddDeliveryCarrierValidationErrors;
    }
  >;
}

export function removeDeliveryCarrier(
  httpClient: HttpClient
): RemoveDeliveryCarrierSignature {
  return (
    deliveryRegionId: string,
    options: NonNullablePaths<RemoveDeliveryCarrierOptions, `appId`, 2>
  ) =>
    universalRemoveDeliveryCarrier(
      deliveryRegionId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface RemoveDeliveryCarrierSignature {
  /**
   * Removes a delivery carrier from a delivery region.
   * @param - Delivery region ID.
   */
  (
    deliveryRegionId: string,
    options: NonNullablePaths<RemoveDeliveryCarrierOptions, `appId`, 2>
  ): Promise<
    NonNullablePaths<
      RemoveDeliveryCarrierResponse,
      `deliveryProfile.deliveryRegions`,
      3
    > & {
      __applicationErrorsType?: RemoveDeliveryCarrierApplicationErrors;
    }
  >;
}

export function updateDeliveryCarrier(
  httpClient: HttpClient
): UpdateDeliveryCarrierSignature {
  return (
    deliveryRegionId: string,
    options?: NonNullablePaths<
      UpdateDeliveryCarrierOptions,
      `deliveryCarrier.appId`,
      3
    >
  ) =>
    universalUpdateDeliveryCarrier(
      deliveryRegionId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdateDeliveryCarrierSignature {
  /**
   * Updates a delivery carrier's settings in a delivery region.
   *
   *
   * This method updates an existing carrier's settings (such as backup rate and additional charges) using a partial update. Only the fields you provide are updated; other carrier settings remain unchanged.
   *
   * To add a new carrier to a region, use [Add Delivery Carrier](https://dev.wix.com/docs/api-reference/business-solutions/e-commerce/shipping-delivery/delivery-profiles/add-delivery-carrier).
   * @param - Delivery region ID.
   */
  (
    deliveryRegionId: string,
    options?: NonNullablePaths<
      UpdateDeliveryCarrierOptions,
      `deliveryCarrier.appId`,
      3
    >
  ): Promise<
    NonNullablePaths<
      UpdateDeliveryCarrierResponse,
      `deliveryProfile.deliveryRegions`,
      3
    > & {
      __applicationErrorsType?: UpdateDeliveryCarrierApplicationErrors;
    }
  >;
}

export function listInstalledDeliveryCarriers(
  httpClient: HttpClient
): ListInstalledDeliveryCarriersSignature {
  return () =>
    universalListInstalledDeliveryCarriers(
      // @ts-ignore
      { httpClient }
    );
}

interface ListInstalledDeliveryCarriersSignature {
  /**
   * Retrieves a list of all delivery carriers installed on a site.
   *
   *
   * Use this method to discover available carriers before adding them to delivery regions.
   */
  (): Promise<
    NonNullablePaths<
      ListInstalledDeliveryCarriersResponse,
      | `installedDeliveryCarriers`
      | `installedDeliveryCarriers.${number}._id`
      | `installedDeliveryCarriers.${number}.displayName`
      | `installedDeliveryCarriers.${number}.toggleGetCarrierSettingsEnabled`,
      4
    >
  >;
}

export function getDeliveryDestinationProperties(
  httpClient: HttpClient
): GetDeliveryDestinationPropertiesSignature {
  return () =>
    universalGetDeliveryDestinationProperties(
      // @ts-ignore
      { httpClient }
    );
}

interface GetDeliveryDestinationPropertiesSignature {
  /**
   * Retrieves delivery destination properties.
   *
   *
   * Returns properties for Rest of World, as well as aggregated country and subdivision data. Properties include whether postal codes are required and whether pickup is the only delivery option for each destination.
   */
  (): Promise<
    NonNullablePaths<
      GetDeliveryDestinationPropertiesResponse,
      | `restOfWorld.postalCodeRequired`
      | `restOfWorld.pickupOnly`
      | `countries`
      | `countries.${number}.country`
      | `countries.${number}.deliveryDestinationProperties.postalCodeRequired`
      | `countries.${number}.deliveryDestinationProperties.pickupOnly`,
      5
    >
  >;
}

export function listDeliveryCarriers(
  httpClient: HttpClient
): ListDeliveryCarriersSignature {
  return (deliveryProfileId: string, options?: ListDeliveryCarriersOptions) =>
    universalListDeliveryCarriers(
      deliveryProfileId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface ListDeliveryCarriersSignature {
  /**
   * Retrieves delivery carrier settings for a delivery profile.
   *
   *
   * Returns detailed carrier configurations, including regional settings for each carrier. This method provides more comprehensive information than [List Installed Delivery Carriers](https://dev.wix.com/docs/api-reference/business-solutions/e-commerce/shipping-delivery/delivery-profiles/list-installed-delivery-carriers).
   * @param - Delivery profile ID.
   */
  (deliveryProfileId: string, options?: ListDeliveryCarriersOptions): Promise<
    NonNullablePaths<
      ListDeliveryCarriersResponse,
      | `results`
      | `results.${number}.deliveryCarrierMetadata.success`
      | `results.${number}.deliveryCarrierMetadata.error.code`
      | `results.${number}.deliveryCarrierMetadata.error.description`
      | `results.${number}.deliveryCarrierDetails._id`
      | `results.${number}.deliveryCarrierDetails.displayName`
      | `results.${number}.deliveryCarrierDetails.toggleGetCarrierSettingsEnabled`
      | `results.${number}.deliveryCarrierDetails.toggleUpdateCarrierActiveStatusEnabled`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`
      | `bulkActionMetadata.undetailedFailures`,
      6
    > & {
      __applicationErrorsType?: ListDeliveryCarriersApplicationErrors;
    }
  >;
}

/** @internal */
export function updateCarrierSettings(
  httpClient: HttpClient
): UpdateCarrierSettingsSignature {
  return (
    carrierId: string,
    options: NonNullablePaths<
      UpdateCarrierSettingsOptions,
      `active` | `rowId`,
      2
    >
  ) =>
    universalUpdateCarrierSettings(
      carrierId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdateCarrierSettingsSignature {
  /**
   * Updates a carrier's external active settings.
   * @param - Carrier app ID.
   */
  (
    carrierId: string,
    options: NonNullablePaths<
      UpdateCarrierSettingsOptions,
      `active` | `rowId`,
      2
    >
  ): Promise<
    void & {
      __applicationErrorsType?: UpdateCarrierSettingsApplicationErrors;
    }
  >;
}

export function setDeliveryCarrierActiveStatus(
  httpClient: HttpClient
): SetDeliveryCarrierActiveStatusSignature {
  return (
    carrierAppId: string,
    options: NonNullablePaths<
      SetDeliveryCarrierActiveStatusOptions,
      `active` | `rowId`,
      2
    >
  ) =>
    universalSetDeliveryCarrierActiveStatus(
      carrierAppId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface SetDeliveryCarrierActiveStatusSignature {
  /**
   * Sets a delivery carrier's active status.
   * @param - Carrier app ID.
   */
  (
    carrierAppId: string,
    options: NonNullablePaths<
      SetDeliveryCarrierActiveStatusOptions,
      `active` | `rowId`,
      2
    >
  ): Promise<
    void & {
      __applicationErrorsType?: SetDeliveryCarrierActiveStatusApplicationErrors;
    }
  >;
}

export function updateExtendedFields(
  httpClient: HttpClient
): UpdateExtendedFieldsSignature {
  return (
    _id: string,
    namespace: string,
    options: NonNullablePaths<UpdateExtendedFieldsOptions, `namespaceData`, 2>
  ) =>
    universalUpdateExtendedFields(
      _id,
      namespace,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdateExtendedFieldsSignature {
  /**
   * Updates extended fields of a delivery profile without incrementing revision.
   * @param - ID of the entity to update.
   * @param - Identifier for the app whose extended fields are being updated.
   */
  (
    _id: string,
    namespace: string,
    options: NonNullablePaths<UpdateExtendedFieldsOptions, `namespaceData`, 2>
  ): Promise<
    NonNullablePaths<
      UpdateExtendedFieldsResponse,
      `deliveryProfile.deliveryRegions`,
      3
    >
  >;
}

export const onDeliveryProfileCreated = EventDefinition(
  'wix.ecom.v1.delivery_profile_created',
  true,
  (event: DeliveryProfileCreatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'entity.deliveryRegions.createdDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<DeliveryProfileCreatedEnvelope>();
export const onDeliveryProfileDeleted = EventDefinition(
  'wix.ecom.v1.delivery_profile_deleted',
  true,
  (event: DeliveryProfileDeletedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'entity.deliveryRegions.createdDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<DeliveryProfileDeletedEnvelope>();
export const onDeliveryProfileRegionAdded = EventDefinition(
  'wix.ecom.v1.delivery_profile_region_added',
  true,
  (event: DeliveryProfileRegionAddedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [{ path: 'data.createdDate' }, { path: 'metadata.eventTime' }],
        },
      ])
    )
)<DeliveryProfileRegionAddedEnvelope>();
export const onDeliveryProfileRegionRemoved = EventDefinition(
  'wix.ecom.v1.delivery_profile_region_removed',
  true,
  (event: DeliveryProfileRegionRemovedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [{ path: 'metadata.eventTime' }],
        },
      ])
    )
)<DeliveryProfileRegionRemovedEnvelope>();
export const onDeliveryProfileRegionUpdated = EventDefinition(
  'wix.ecom.v1.delivery_profile_region_updated',
  true,
  (event: DeliveryProfileRegionUpdatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [{ path: 'data.createdDate' }, { path: 'metadata.eventTime' }],
        },
      ])
    )
)<DeliveryProfileRegionUpdatedEnvelope>();
export const onDeliveryProfileUpdated = EventDefinition(
  'wix.ecom.v1.delivery_profile_updated',
  true,
  (event: DeliveryProfileUpdatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'entity.deliveryRegions.createdDate' },
            { path: 'metadata.eventTime' },
            { path: 'modifiedFields.createdDate' },
            { path: 'modifiedFields.updatedDate' },
            { path: 'modifiedFields.deliveryRegions.createdDate' },
          ],
        },
      ])
    )
)<DeliveryProfileUpdatedEnvelope>();

export {
  AccountInfo,
  AccountInfoMetadata,
  ActionEvent,
  AddDeliveryCarrierOptions,
  AddDeliveryCarrierRequest,
  AddDeliveryCarrierResponse,
  AddDeliveryRegionOptions,
  AddDeliveryRegionRequest,
  AddDeliveryRegionResponse,
  AdditionalCharge,
  ApplicationError,
  Asset,
  BackupRate,
  BaseEventMetadata,
  BulkActionMetadata,
  Column,
  CountryDeliveryProperties,
  CreateDeliveryProfileRequest,
  CreateDeliveryProfileResponse,
  CreatedBy,
  CreatedByIdOneOf,
  CursorPaging,
  CursorPagingMetadata,
  CursorQuery,
  CursorQueryPagingMethodOneOf,
  Cursors,
  DashboardTable,
  DeleteContext,
  DeleteDefaultDeliveryProfileRequest,
  DeleteDefaultDeliveryProfileResponse,
  DeleteDeliveryProfileRequest,
  DeleteDeliveryProfileResponse,
  DeleteStatus,
  DeliveryCarrier,
  DeliveryCarrierDetails,
  DeliveryCarrierRegionalSettings,
  DeliveryDestinationProperties,
  DeliveryProfile,
  DeliveryProfileCreatedEnvelope,
  DeliveryProfileDeletedEnvelope,
  DeliveryProfileQuerySpec,
  DeliveryProfileRegionAddedEnvelope,
  DeliveryProfileRegionRemovedEnvelope,
  DeliveryProfileRegionUpdatedEnvelope,
  DeliveryProfileUpdatedEnvelope,
  DeliveryProfilesQueryBuilder,
  DeliveryProfilesQueryResult,
  DeliveryRegion,
  DeliveryRegionAdded,
  DeliveryRegionRemoved,
  DeliveryRegionUpdated,
  Destination,
  DestinationScope,
  DomainEvent,
  DomainEventBodyOneOf,
  EditorlessAssigned,
  EditorlessUnassigned,
  Empty,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  EventMetadata,
  ExtendedFields,
  GetDeliveryDestinationPropertiesRequest,
  GetDeliveryDestinationPropertiesResponse,
  GetDeliveryProfileByDeliveryRegionIdRequest,
  GetDeliveryProfileByDeliveryRegionIdResponse,
  GetDeliveryProfileRequest,
  GetDeliveryProfileResponse,
  IdentificationData,
  IdentificationDataIdOneOf,
  InstalledDeliveryCarrier,
  ItemMetadata,
  ListDeliveryCarriersOptions,
  ListDeliveryCarriersRequest,
  ListDeliveryCarriersResponse,
  ListDeliveryCarriersResult,
  ListDeliveryDestinationsRequest,
  ListDeliveryDestinationsResponse,
  ListInstalledDeliveryCarriersRequest,
  ListInstalledDeliveryCarriersResponse,
  MessageEnvelope,
  MetaSiteSpecialEvent,
  MetaSiteSpecialEventPayloadOneOf,
  Namespace,
  NamespaceChanged,
  OdeditorAssigned,
  OdeditorUnassigned,
  PicassoAssigned,
  PicassoUnassigned,
  QueryDeliveryProfilesRequest,
  QueryDeliveryProfilesResponse,
  RemoveDeliveryCarrierOptions,
  RemoveDeliveryCarrierRequest,
  RemoveDeliveryCarrierResponse,
  RemoveDeliveryRegionIdentifiers,
  RemoveDeliveryRegionOptions,
  RemoveDeliveryRegionRequest,
  RemoveDeliveryRegionResponse,
  RequiredZipcodeRequest,
  RequiredZipcodeResponse,
  RestoreInfo,
  Row,
  ServiceProvisioned,
  ServiceRemoved,
  SetDeliveryCarrierActiveStatusOptions,
  SetDeliveryCarrierActiveStatusRequest,
  SetDeliveryCarrierActiveStatusResponse,
  SiteCreated,
  SiteCreatedContext,
  SiteDeleted,
  SiteHardDeleted,
  SiteMarkedAsTemplate,
  SiteMarkedAsWixSite,
  SitePublished,
  SitePurgedExternally,
  SiteRenamed,
  SiteTransferred,
  SiteUndeleted,
  SiteUnpublished,
  SiteUrlChanged,
  SortOrder,
  Sorting,
  State,
  StudioAssigned,
  StudioTwoAssigned,
  StudioTwoUnassigned,
  StudioUnassigned,
  SubdivisionDeliveryProperties,
  UpdateCarrierSettingsOptions,
  UpdateCarrierSettingsRequest,
  UpdateCarrierSettingsResponse,
  UpdateDeliveryCarrierOptions,
  UpdateDeliveryCarrierRequest,
  UpdateDeliveryCarrierResponse,
  UpdateDeliveryProfile,
  UpdateDeliveryProfileRequest,
  UpdateDeliveryProfileResponse,
  UpdateDeliveryRegionIdentifiers,
  UpdateDeliveryRegionOptions,
  UpdateDeliveryRegionRequest,
  UpdateDeliveryRegionResponse,
  UpdateExtendedFieldsOptions,
  UpdateExtendedFieldsRequest,
  UpdateExtendedFieldsResponse,
  UserDomainMediaDisabled,
  UserDomainMediaEnabled,
  WebhookIdentityType,
  WixelAssigned,
  WixelUnassigned,
  utils,
} from './ecom-v1-delivery-profile-delivery-profile.universal.js';
